import {
  canonicalLoomDefinitionJson,
  parseLoomAcceptedDefinition,
} from "./loomAcceptedDefinitionBinding";
import { parseLoomWingBodyDefinitionV2 } from "../../api/loomRebuildV3";

export interface LoomAcceptedDefinitionBinding {
  evaluatorId: "loom.native-wing-body/v2" | "loom.native-wing-body/v3";
  definitionRevision: string;
  definition: Record<string, unknown>;
}

export interface LoomV3SourceReceipt {
  adapter: "openvsp-vsp3";
  sourceAssetId: string;
  sourceAssetSha256: string;
  selectedComponents: [
    { componentId: string; role: "wing" },
    { componentId: string; role: "fuselage" },
  ];
  normalization: {
    policyId: string;
    sourceLengthUnit: "unbound";
    sourceBodyLengthNative: number;
    targetBodyLengthM: number;
    metresPerNativeUnit: number;
  };
}

export interface LoomV3AdaptationReceipt {
  adaptationId: string;
  kind: "fuselage.end-cap.explicit-flat";
  sourceNodeId: string;
  sourceStationId: string;
  end: "nose" | "tail";
  sourceProfileRef: string;
  sourceValue: "open-end";
  derivedValue: "planar-cap";
  reason: "The selected finite source end ring is open; the evaluator closes it with a planar cap matching the axial-slab boundary.";
}

export interface LoomAcceptedDefinitionEvidence {
  definitionBinding?: LoomAcceptedDefinitionBinding;
  unionAlgorithmId?: "loom.implicit-guided-explicit-union/v1";
  sourceReceipt?: LoomV3SourceReceipt;
  adaptationReceipts?: LoomV3AdaptationReceipt[];
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function exact(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  const result = record(value, label); const actual = Object.keys(result);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label} has an unsupported field set`);
  }
  return result;
}

function token(value: unknown, label: string): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/.test(value)) {
    throw new Error(`${label} is not a bounded token`);
  }
  return value;
}

function identifier(value: unknown, label: string): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/.test(value)) {
    throw new Error(`${label} is not a bounded identifier`);
  }
  return value;
}

function revision(value: unknown, label: string): string {
  if (typeof value !== "string" || !/^sha256:[0-9a-f]{64}$/.test(value)) {
    throw new Error(`${label} must be a sha256 revision`);
  }
  return value;
}

function positive(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be positive and finite`);
  }
  return value;
}

function sourceReceipt(value: unknown): LoomV3SourceReceipt {
  const raw = exact(value, ["adapter", "sourceAssetId", "sourceAssetSha256", "selectedComponents", "normalization"], "acceptedGeometry.sourceReceipt");
  const components = raw.selectedComponents;
  if (raw.adapter !== "openvsp-vsp3" || !Array.isArray(components)
      || components.length !== 2) throw new Error("v3 source receipt is unsupported");
  const selected = (["wing", "fuselage"] as const).map((role, index) => {
    const item = exact(components[index], ["componentId", "role"], `sourceReceipt.selectedComponents[${index}]`);
    if (item.role !== role) throw new Error("v3 selected components must be ordered wing then fuselage");
    return { componentId: token(item.componentId, "source component ID"), role };
  }) as LoomV3SourceReceipt["selectedComponents"];
  if (selected[0].componentId === selected[1].componentId) throw new Error("v3 selected component IDs collide");
  const normalization = exact(raw.normalization, ["policyId", "sourceLengthUnit", "sourceBodyLengthNative", "targetBodyLengthM", "metresPerNativeUnit"], "sourceReceipt.normalization");
  if (normalization.sourceLengthUnit !== "unbound") throw new Error("v3 source length unit cannot be claimed as bound");
  const sourceBodyLengthNative = positive(normalization.sourceBodyLengthNative, "source body length");
  const targetBodyLengthM = positive(normalization.targetBodyLengthM, "target body length");
  const metresPerNativeUnit = positive(normalization.metresPerNativeUnit, "source scale");
  if (Math.abs(targetBodyLengthM / sourceBodyLengthNative - metresPerNativeUnit)
      > Math.max(1e-15, Math.abs(metresPerNativeUnit) * 1e-12)) {
    throw new Error("v3 source normalization receipt is inconsistent");
  }
  return {
    adapter: "openvsp-vsp3", sourceAssetId: token(raw.sourceAssetId, "source asset ID"),
    sourceAssetSha256: revision(raw.sourceAssetSha256, "source asset hash"), selectedComponents: selected,
    normalization: { policyId: identifier(normalization.policyId, "normalization policy ID"), sourceLengthUnit: "unbound", sourceBodyLengthNative, targetBodyLengthM, metresPerNativeUnit },
  };
}

const ADAPTATION_REASON = "The selected finite source end ring is open; the evaluator closes it with a planar cap matching the axial-slab boundary." as const;

function expectedAdaptations(definition: Record<string, unknown>): LoomV3AdaptationReceipt[] {
  const fuselage = exact(definition.fuselage, ["sourceNodeId", "stations", "sourceEndCaps", "endCaps"], "v3 definition fuselage");
  const sourceNodeId = token(fuselage.sourceNodeId, "fuselage source node ID");
  const stations = fuselage.stations;
  if (!Array.isArray(stations) || stations.length < 2) throw new Error("v3 stations are malformed");
  const sourceCaps = exact(fuselage.sourceEndCaps, ["noseOption", "tailOption"], "v3 source end-cap receipt");
  const caps = exact(fuselage.endCaps, ["nose", "tail"], "v3 end-cap policy");
  const result: LoomV3AdaptationReceipt[] = [];
  (["nose", "tail"] as const).forEach((end, position) => {
    const sourceOption = sourceCaps[end === "nose" ? "noseOption" : "tailOption"];
    if (sourceOption !== 0 && sourceOption !== 1) throw new Error("v3 source cap option is unsupported");
    if (caps[end] === "point" && sourceOption === 1) {
      throw new Error("a point endpoint cannot claim a flat source cap");
    }
    if (caps[end] === "point") return;
    if (caps[end] !== "flat") throw new Error("v3 end-cap policy is unsupported");
    if (sourceOption === 1) return;
    const station = exact(stations[position === 0 ? 0 : stations.length - 1], ["stationId", "kind", "xM", "profileRef"], `v3 ${end} station`);
    if (station.kind !== "profile") throw new Error("v3 flat cap must bind a finite profile");
    result.push({
      adaptationId: `${sourceNodeId}:${end}:explicit-flat-cap`, kind: "fuselage.end-cap.explicit-flat",
      sourceNodeId, sourceStationId: token(station.stationId, `${end} station ID`), end,
      sourceProfileRef: token(station.profileRef, `${end} profile reference`),
      sourceValue: "open-end", derivedValue: "planar-cap", reason: ADAPTATION_REASON,
    });
  });
  return result;
}

export function parseLoomAcceptedDefinitionEvidence(
  schema: "loom.accepted-mesh/v1" | "loom.accepted-mesh/v2" | "loom.accepted-mesh/v3",
  raw: Record<string, unknown>,
): LoomAcceptedDefinitionEvidence {
  if (schema === "loom.accepted-mesh/v1") return {};
  const bindingRaw = exact(raw.definitionBinding, ["evaluatorId", "definitionRevision", "definition"], "acceptedGeometry.definitionBinding");
  const definition = parseLoomAcceptedDefinition(bindingRaw.definition);
  const expectedEvaluator = schema === "loom.accepted-mesh/v2" ? "loom.native-wing-body/v2" : "loom.native-wing-body/v3";
  const expectedDefinition = schema === "loom.accepted-mesh/v2" ? "loom.wing-body-definition/v1" : "loom.wing-body-definition/v2";
  if (bindingRaw.evaluatorId !== expectedEvaluator || definition.schema !== expectedDefinition) {
    throw new Error("acceptedGeometry definition binding evaluator or schema is unsupported");
  }
  if (schema === "loom.accepted-mesh/v3") parseLoomWingBodyDefinitionV2(definition);
  const definitionBinding: LoomAcceptedDefinitionBinding = {
    evaluatorId: expectedEvaluator, definitionRevision: revision(bindingRaw.definitionRevision, "definition revision"), definition,
  };
  if (schema === "loom.accepted-mesh/v2") return { definitionBinding };
  if (raw.unionAlgorithmId !== "loom.implicit-guided-explicit-union/v1") {
    throw new Error("v3 union algorithm receipt is unsupported");
  }
  const source = sourceReceipt(raw.sourceReceipt);
  if (canonicalLoomDefinitionJson(source) !== canonicalLoomDefinitionJson(definition.sourceBinding)) {
    throw new Error("v3 source receipt does not equal the revision-bound source binding");
  }
  if (!Array.isArray(raw.adaptationReceipts)) throw new Error("v3 adaptation receipts must be an array");
  const expected = expectedAdaptations(definition);
  if (canonicalLoomDefinitionJson(raw.adaptationReceipts) !== canonicalLoomDefinitionJson(expected)) {
    throw new Error("v3 adaptation receipts do not match the explicit end-cap policy");
  }
  return { definitionBinding, unionAlgorithmId: "loom.implicit-guided-explicit-union/v1", sourceReceipt: source, adaptationReceipts: expected };
}
